import { Link, Navigate, useParams } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { SiteLayout } from "@/components/SiteChrome";
import { DORA_IMAGE, HERO_IMAGE, internalPages, SITE_URL, WHATSAPP_SCHEDULE } from "@/data/siteData";

const makeSchemas = (page) => [
  { "@context": "https://schema.org", "@type": "WebPage", name: page.title, description: page.description, url: `${SITE_URL}${page.path}/`, isPartOf: { "@type": "WebSite", name: "Domus Dora Idiomas", url: SITE_URL } },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Início", item: `${SITE_URL}/` }, { "@type": "ListItem", position: 2, name: page.title, item: `${SITE_URL}${page.path}/` }] },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: page.faqs.map(([question, answer]) => ({ "@type": "Question", name: question, acceptedAnswer: { "@type": "Answer", text: answer } })) },
];

export default function InternalPage() {
  const { slug: pageKey } = useParams();
  const page = internalPages[pageKey];
  if (!page) return <Navigate to="/" replace />;
  if (pageKey === "sobre") return <AboutPage page={page} pageKey={pageKey} />;
  if (["cursos", "como-funciona", "contato"].includes(pageKey)) return <PremiumPage page={page} pageKey={pageKey} />;
  return (
    <SiteLayout>
      <Seo title={`${page.title} | Domus Dora`} description={page.description} path={page.path} schemas={makeSchemas(page)} />
      <section className="internal-hero" data-testid={`${pageKey}-hero-section`}>
        <div className="absolute inset-0"><img src={HERO_IMAGE} alt="Curso online da Domus Dora" className="w-full h-full object-cover" /><div className="absolute inset-0 hero-gradient" /><div className="absolute inset-0 bg-[var(--dark-background-color)] opacity-50" /></div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 pt-28">
          <nav aria-label="Breadcrumb" className="breadcrumb-light" data-testid={`${pageKey}-breadcrumb`}><Link to="/" data-testid={`${pageKey}-breadcrumb-home`}>Início</Link><i className="fas fa-chevron-right" /><span>{page.eyebrow}</span></nav>
          <div className="max-w-4xl"><div className="hero-badge" data-testid={`${pageKey}-hero-badge`}><i className="fas fa-star" /><span>{page.eyebrow}</span></div><h1 className="internal-title" data-testid={`${pageKey}-title`}>{page.title}</h1><p className="internal-lead" data-testid={`${pageKey}-description`}>{page.intro}</p><a href={WHATSAPP_SCHEDULE} target="_blank" rel="noreferrer" className="hero-cta" data-testid={`${pageKey}-hero-cta`}><i className="fab fa-whatsapp text-2xl" />Agende sua aula<i className="fas fa-arrow-right" /></a></div>
        </div>
      </section>
      <section className="py-20 lg:py-32 bg-[var(--light-background-color)] relative overflow-hidden" data-testid={`${pageKey}-content-section`}><div className="container mx-auto px-4 lg:px-8"><div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"><div><div className="section-badge"><i className="fas fa-heart" /><span>Domus Dora</span></div><h2 className="section-title" data-testid={`${pageKey}-content-title`}>Ensino com atenção <span>individual</span></h2>{page.paragraphs.map((text, index) => <p key={text} className="body-copy" data-testid={`${pageKey}-paragraph-${index + 1}`}>{text}</p>)}</div><div className="relative"><div className="absolute -top-6 -left-6 w-full h-full border-4 border-[var(--primary-color)] rounded-2xl opacity-30" /><img src={DORA_IMAGE} alt="Professora Dora Domus" className="relative rounded-2xl shadow-2xl w-full" data-testid={`${pageKey}-dora-image`} /></div></div></div></section>
      <section className="py-20 lg:py-32 bg-white diagonal-pattern" data-testid={`${pageKey}-cards-section`}><div className="container mx-auto px-4 lg:px-8"><div className="section-heading"><div className="light-badge"><i className="fas fa-graduation-cap" /><span>Informações principais</span></div><h2 className="section-title" data-testid={`${pageKey}-cards-title`}>Um percurso feito para <span>você</span></h2></div><div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{page.cards.map(([title, text], index) => <article key={title} className="content-card" data-testid={`${pageKey}-card-${index + 1}`}><div className="icon-circle"><i className="fas fa-check" /></div><h3>{title}</h3><p>{text}</p></article>)}</div></div></section>
      <FaqSection pageKey={pageKey} faqs={page.faqs} />
    </SiteLayout>
  );
}

const AboutPage = ({ page, pageKey }) => (
  <SiteLayout>
    <Seo title={`${page.title} | Domus Dora`} description={page.description} path={page.path} schemas={makeSchemas(page)} />
    <section className="about-hero" data-testid={`${pageKey}-hero-section`}>
      <div className="about-hero-orb about-hero-orb-one" />
      <div className="about-hero-orb about-hero-orb-two" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <nav aria-label="Breadcrumb" className="about-breadcrumb" data-testid={`${pageKey}-breadcrumb`}><Link to="/" data-testid={`${pageKey}-breadcrumb-home`}>Início</Link><i className="fas fa-chevron-right" /><span>{page.eyebrow}</span></nav>
        <div className="about-hero-grid">
          <div className="about-hero-copy">
            <div className="about-kicker" data-testid={`${pageKey}-hero-badge`}><i className="fas fa-feather-alt" /><span>Italiano com escuta, cultura e direção</span></div>
            <h1 data-testid={`${pageKey}-title`}>Dora Domus transforma o estudo do italiano em um encontro pessoal.</h1>
            <p data-testid={`${pageKey}-description`}>{page.intro}</p>
            <div className="about-hero-actions">
              <a href={WHATSAPP_SCHEDULE} target="_blank" rel="noreferrer" className="hero-cta cta-glow" data-testid={`${pageKey}-hero-cta`}><i className="fab fa-whatsapp text-2xl" />Agende sua aula</a>
              <a href="#metodo-dora" className="about-secondary-link"><i className="fas fa-arrow-down" />Conheça o método</a>
            </div>
          </div>
          <div className="about-portrait-wrap" aria-label="Foto da professora Dora Domus">
            <div className="about-portrait-frame">
              <img src={DORA_IMAGE} alt="Professora Dora Domus" data-testid={`${pageKey}-dora-image`} />
            </div>
            <div className="about-signature-card">
              <span>Professora particular</span>
              <strong>Dora Domus</strong>
              <p>Letras pela USP, aulas individuais online e material pensado para cada aluno.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section id="metodo-dora" className="about-story" data-testid={`${pageKey}-content-section`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="about-story-grid">
          <div>
            <div className="section-badge"><i className="fas fa-heart" /><span>Domus Dora</span></div>
            <h2 className="section-title" data-testid={`${pageKey}-content-title`}>Uma aula que começa pela <span>sua história</span></h2>
            {page.paragraphs.map((text, index) => <p key={text} className="body-copy" data-testid={`${pageKey}-paragraph-${index + 1}`}>{text}</p>)}
          </div>
          <div className="about-method-card">
            <span className="about-method-number">01</span>
            <h3>Diagnóstico cuidadoso</h3>
            <p>Antes de escolher exercícios, Dora entende repertório, rotina, inseguranças e objetivo real do aluno.</p>
            <span className="about-method-number">02</span>
            <h3>Material com intenção</h3>
            <p>Textos, conversas e atividades são selecionados para gerar progresso visível, sem conteúdo genérico.</p>
          </div>
        </div>
      </div>
    </section>
    <section className="about-pillars" data-testid={`${pageKey}-cards-section`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="section-heading">
          <div className="light-badge"><i className="fas fa-graduation-cap" /><span>Informações principais</span></div>
          <h2 className="section-title" data-testid={`${pageKey}-cards-title`}>O que torna o percurso <span>diferente</span></h2>
        </div>
        <div className="about-pillars-grid">{page.cards.map(([title, text], index) => <article key={title} className="about-pillar-card" data-testid={`${pageKey}-card-${index + 1}`}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>)}</div>
      </div>
    </section>
    <FaqSection pageKey={pageKey} faqs={page.faqs} />
  </SiteLayout>
);

const premiumContent = {
  cursos: {
    icon: "fa-book-open",
    kicker: "Percursos individuais",
    title: "Cursos desenhados para o seu objetivo, não para uma turma genérica.",
    description: "Italiano, literatura, escrita e apoio escolar ganham um plano próprio, com materiais escolhidos para o nível, a rotina e o interesse de cada estudante.",
    anchor: "Ver percursos",
    sectionTitle: <>Escolha um caminho com <span>propósito</span></>,
    sideTitle: "O que muda na prática?",
    sideItems: ["Diagnóstico antes do plano", "Ritmo individual", "Material selecionado", "Feedback direto"],
  },
  "como-funciona": {
    icon: "fa-route",
    kicker: "Método claro",
    title: "Do primeiro contato ao plano de estudos, cada etapa tem intenção.",
    description: "A aula online mantém proximidade, escuta e personalização. Você entende como começa, como evolui e como acompanha seu próprio progresso.",
    anchor: "Ver etapas",
    sectionTitle: <>Um processo simples, humano e <span>bem guiado</span></>,
    sideTitle: "Experiência da aula",
    sideItems: ["Encontro ao vivo", "Aula individual", "Plano ajustável", "Material incluído"],
  },
  contato: {
    icon: "fa-comments",
    kicker: "Conversa direta",
    title: "Fale com a Dora e descubra qual percurso combina com você.",
    description: "O atendimento é direto pelo WhatsApp para entender seu objetivo, tirar dúvidas e combinar uma aula experimental com tranquilidade.",
    anchor: "Ver contatos",
    sectionTitle: <>Um primeiro contato <span>leve e objetivo</span></>,
    sideTitle: "Canais e horários",
    sideItems: ["WhatsApp direto", "Segunda a sexta", "08h às 18h", "Atendimento online"],
  },
};

const PremiumPage = ({ page, pageKey }) => {
  const content = premiumContent[pageKey];
  return (
    <SiteLayout>
      <Seo title={`${page.title} | Domus Dora`} description={page.description} path={page.path} schemas={makeSchemas(page)} />
      <section className={`premium-hero premium-hero-${pageKey}`} data-testid={`${pageKey}-hero-section`}>
        <div className="premium-noise" />
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <nav aria-label="Breadcrumb" className="about-breadcrumb" data-testid={`${pageKey}-breadcrumb`}><Link to="/" data-testid={`${pageKey}-breadcrumb-home`}>Início</Link><i className="fas fa-chevron-right" /><span>{page.eyebrow}</span></nav>
          <div className="premium-hero-grid">
            <div className="premium-hero-copy">
              <div className="about-kicker" data-testid={`${pageKey}-hero-badge`}><i className={`fas ${content.icon}`} /><span>{content.kicker}</span></div>
              <h1 data-testid={`${pageKey}-title`}>{content.title}</h1>
              <p data-testid={`${pageKey}-description`}>{content.description}</p>
              <div className="about-hero-actions">
                <a href={WHATSAPP_SCHEDULE} target="_blank" rel="noreferrer" className="hero-cta cta-glow" data-testid={`${pageKey}-hero-cta`}><i className="fab fa-whatsapp text-2xl" />Agende sua aula</a>
                <a href={`#${pageKey}-detalhes`} className="about-secondary-link"><i className="fas fa-arrow-down" />{content.anchor}</a>
              </div>
            </div>
            <div className="premium-feature-panel">
              <span className="premium-panel-label">Domus Dora</span>
              <h2>{content.sideTitle}</h2>
              <div className="premium-panel-list">{content.sideItems.map((item) => <span key={item}><i className="fas fa-check" />{item}</span>)}</div>
            </div>
          </div>
        </div>
      </section>
      <section id={`${pageKey}-detalhes`} className="premium-section" data-testid={`${pageKey}-content-section`}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="premium-section-head">
            <div className="section-badge"><i className="fas fa-heart" /><span>Domus Dora</span></div>
            <h2 className="section-title" data-testid={`${pageKey}-content-title`}>{content.sectionTitle}</h2>
            {page.paragraphs.map((text, index) => <p key={text} className="body-copy" data-testid={`${pageKey}-paragraph-${index + 1}`}>{text}</p>)}
          </div>
          <div className="premium-card-grid">{page.cards.map(([title, text], index) => <article key={title} className="premium-card" data-testid={`${pageKey}-card-${index + 1}`}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>)}</div>
        </div>
      </section>
      {pageKey === "contato" && <ContactShowcase />}
      <FaqSection pageKey={pageKey} faqs={page.faqs} />
    </SiteLayout>
  );
};

const ContactShowcase = () => (
  <section className="contact-showcase">
    <div className="container mx-auto px-4 lg:px-8">
      <div className="contact-showcase-card">
        <div><span>Atendimento direto</span><h2>Vamos conversar sobre seu objetivo?</h2><p>Envie uma mensagem contando seu nível, disponibilidade e motivo para estudar. A Dora responde diretamente para orientar o melhor começo.</p></div>
        <a href={WHATSAPP_SCHEDULE} target="_blank" rel="noreferrer"><i className="fab fa-whatsapp" />Chamar no WhatsApp</a>
      </div>
    </div>
  </section>
);

export const FaqSection = ({ pageKey, faqs }) => (
  <section className="py-20 lg:py-32 bg-gradient-to-br from-[var(--secondary-button-bg-color)] to-white" data-testid={`${pageKey}-faq-section`}><div className="container mx-auto px-4 lg:px-8"><div className="section-heading"><div className="light-badge"><i className="fas fa-question-circle" /><span>Perguntas frequentes</span></div><h2 className="section-title" data-testid={`${pageKey}-faq-title`}>Dúvidas sobre as <span>aulas</span></h2></div><div className="max-w-4xl mx-auto space-y-4">{faqs.map(([question, answer], index) => <details key={question} className="faq-item" data-testid={`${pageKey}-faq-item-${index + 1}`}><summary data-testid={`${pageKey}-faq-question-${index + 1}`}>{question}<i className="fas fa-chevron-down" /></summary><p data-testid={`${pageKey}-faq-answer-${index + 1}`}>{answer}</p></details>)}</div><div className="text-center mt-10"><a href={WHATSAPP_SCHEDULE} target="_blank" rel="noreferrer" className="primary-link" data-testid={`${pageKey}-faq-cta`}><i className="fab fa-whatsapp text-xl" />Fale com a Dora</a></div></div></section>
);
